import { Fragment, useCallback } from 'react';
import type { ReactNode } from 'react';

import { Menu as BaseMenu } from '@base-ui/react/menu';

import { SelectCheckMark } from '../Select/SelectCheckMark';
import { useInheritedDarkMode } from '../Theme';
import { cn } from '../utils';
import {
  menuHeaderDescriptionRecipe,
  menuHeaderRecipe,
  menuItemCheckRecipe,
  menuItemDescriptionRecipe,
  menuItemIconRecipe,
  menuItemLabelRecipe,
  menuItemRecipe,
  menuItemTextRecipe,
  menuPanelRecipe,
  menuPositionerRecipe,
  menuSeparatorRecipe,
} from './Menu.recipe';

import type { MenuProps } from './types';

/**
 * The rows' container. With a `header` it is a `Menu.Group` whose `Menu.GroupLabel` is the header,
 * so the rows are announced as a group named by it ("Lynsey Enters, lynsey@natter.co") rather than
 * the header being a stray non-item inside `role="menu"`. The separator below the header is the
 * same group-break line the rows use.
 *
 * A selection list's rows are `RadioItem`s, which must sit inside a `RadioGroup` that owns the value:
 * `selectedId` is the selected row's `id`, `null` when none is (a picker can open with nothing chosen
 * yet), and `undefined` for an action menu. `onValueChange` is not wired: each row's own `onSelect`
 * reports the choice.
 */
const MenuRows = ({
  header,
  search,
  selectedId,
  children,
}: Pick<MenuProps, 'header'> & { search: boolean; selectedId: string | null | undefined; children: ReactNode }) => {
  const rows =
    selectedId === undefined ? (
      children
    ) : (
      <BaseMenu.RadioGroup value={selectedId} className="contents">
        {children}
      </BaseMenu.RadioGroup>
    );

  return (
    <div role={search ? 'menu' : undefined}>
      {header ? (
        <BaseMenu.Group className="flex flex-col gap-[2px]">
          <BaseMenu.GroupLabel className={menuHeaderRecipe()} data-part="menu-header">
            <span className="truncate">{header.title}</span>
            {header.description && <span className={menuHeaderDescriptionRecipe()}>{header.description}</span>}
          </BaseMenu.GroupLabel>
          <BaseMenu.Separator className={cn(menuSeparatorRecipe(), 'mt-0')} />
          {rows}
        </BaseMenu.Group>
      ) : (
        <div className="flex flex-col gap-[2px]">{rows}</div>
      )}
    </div>
  );
};

/**
 * Design System 2.0 action menu — the "more actions" popover.
 *
 * Ported from the `Menu` component (file 5aSkE0U4TeWbuIZDvccHjc, node 556:20625 — it stopped being
 * a component set when the `Selection` axis was removed, and the old `556:20624` is gone). Read the
 * `.ds-menu` block header in `design-system.css` before changing anything visual: that component is
 * a SELECTION list, so the separator and the destructive row here are extrapolated from the design
 * system's own precedents rather than authored, and are flagged for a designer.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer, and
 * because the panel is portalled that ancestor is re-resolved from the anchor on every open — see
 * `useInheritedDarkMode`.
 *
 * ── Anchored, not triggered ─────────────────────────────────────────────────────────────────────
 *
 * Base UI's `Menu` is ordinarily `Root > Trigger + Portal`, which would require every call site to
 * move its trigger inside the menu. `Menu.Positioner` takes an `anchor` element instead, so this
 * composition has NO `Menu.Trigger` and the trigger stays wherever the call site already renders it.
 * That is what lets the legacy `common/components/Menu` — a controlled `anchor` / `onClose` pair
 * whose trigger is a sibling — swap to this without a single call site changing.
 *
 * The trade is focus restoration: with no trigger, Base UI has nothing to return focus to on close.
 * Not a regression, because the legacy menu already sets `disableRestoreFocus`.
 *
 * Distinct from `Select`, which renders the same panel and rows but as a value picker: its rows have
 * a selected state and a trailing indicator, and this has neither, because an action menu's rows run
 * a command and close.
 *
 * Controlled usage requires `open`, `anchor`, `onOpenChange` and `options`. Each option needs an
 * `id` and `label`. For a Delete action after ordinary rows:
 *
 * `options={[{ id: 'edit', label: 'Edit' },
 *            { id: 'delete', label: 'Delete', tone: 'destructive', separatorBefore: true }]}`
 *
 * `separatorBefore` draws a line ABOVE its row, so put it on the first row of a group. It is
 * ignored on the first row of the menu.
 *
 * The destructive treatment and separator are supported in code but extrapolated from Figma;
 * the Menu design does not draw these action states. They await HNS-363 design sign-off.
 */
export const Menu = ({
  open,
  anchor,
  onOpenChange,
  onOpenChangeComplete,
  options,
  header,
  search,
  status,
  side = 'bottom',
  align = 'center',
  modal = true,
  className,
}: MenuProps) => {
  /**
   * The anchor doubles as the theme sentinel. It is a real node at the call site's position in the
   * tree, which is exactly what the hook needs to walk up from — so unlike `Dialog` and `Select`,
   * this component renders no hidden sentinel element of its own.
   */
  const { inheritedDarkClass } = useInheritedDarkMode(open, anchor);
  /**
   * Any row carrying `selected` (true OR false) makes the menu a single-choice list — see
   * `MenuOption.selected`. `undefined` means an ordinary action menu.
   */
  const selectedId = options.some((option) => option.selected !== undefined)
    ? (options.find((option) => option.selected)?.id ?? null)
    : undefined;

  // Base UI hands out an `eventDetails` second argument that the legacy API has no use for.
  const handleOpenChange = useCallback((nextOpen: boolean) => onOpenChange(nextOpen), [onOpenChange]);

  return (
    <BaseMenu.Root
      open={open}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      modal={modal}
    >
      {/* The theme class goes on the PORTAL, which is an ancestor of the panel — so the panel carries
          only its own `ds-menu` slot class and the ordinary `:where(.dark) .ds-menu` rule reaches it,
          exactly as for a menu that never portalled. */}
      <BaseMenu.Portal className={inheritedDarkClass}>
        <BaseMenu.Positioner
          anchor={anchor}
          side={side}
          align={align}
          // Shared popup spacing: 4px of air between the trigger and the panel.
          sideOffset={4}
          className={menuPositionerRecipe()}
        >
          {/*
              The panel is portalled into `<body>`, but React synthetic events propagate up the
              REACT tree rather than the DOM one — so without this a chosen row reaches whatever
              `onClick` the CALL SITE sits inside. Every clickable-row consumer depends on it:
              `ExpandableTableRow` puts `onRowClick` on the `<tr>` and inspects nothing, so choosing
              "Copy link" in a My Events row also navigated to the event details page.

              On the popup rather than per row, matching the legacy MUI menu this replaced
              (`common/components/Menu/Menu.tsx`) — that also covers the panel's own padding and its
              separators, which are not rows and bubbled just the same.

              No `preventDefault` to go with it, unlike `ButtonMenu`'s trigger: that one sits inside
              the clickable card in the DOM and so has a real `<a>` default to cancel. This panel is
              portalled out to `<body>` and has no anchor ancestor, so there is nothing to prevent —
              and cancelling the default here would break ordinary clicks on anything a row renders.

              Base UI's dismissal is untouched: `useDismiss` listens on `document` in the CAPTURE
              phase, which runs before the event reaches its target.
          */}
          <BaseMenu.Popup
            {...(search || status ? { role: 'group' as const } : {})}
            className={cn(menuPanelRecipe(), className)}
            data-part="menu"
            onClick={(event) => event.stopPropagation()}
          >
            {search && (
              <div
                onKeyDown={(event) => {
                  // Leave typing and text-editing keys with Search instead of the menu's typeahead.
                  // Up/Down enter the action list; Escape still closes an empty Search, and Tab leaves.
                  if (!['ArrowDown', 'ArrowUp', 'Escape', 'Tab'].includes(event.key)) event.stopPropagation();
                }}
              >
                {search}
              </div>
            )}
            {status ? (
              <div data-part="menu-status">{status}</div>
            ) : (
              <MenuRows header={header} search={!!search} selectedId={selectedId}>
                {options.map(
                  ({ id, label, description, icon, tone, disabled, onSelect, separatorBefore, ...option }, index) => {
                    const content = (
                      <>
                        {icon && (
                          <span className={menuItemIconRecipe()} data-part="item-icon">
                            {icon}
                          </span>
                        )}
                        {/* The column is rendered whether or not there is a description, so a row's
                        label sits in the same box either way — see `menuItemTextRecipe`. Both lines
                        are inside the ITEM rather than beside it, which is what makes the
                        description part of the row's accessible name: a screen reader announces the
                        consequence along with the action. */}
                        <span className={menuItemTextRecipe()}>
                          <span className={menuItemLabelRecipe()}>{label}</span>
                          {description && (
                            <span className={menuItemDescriptionRecipe()} data-part="item-description">
                              {description}
                            </span>
                          )}
                        </span>
                      </>
                    );

                    const itemProps = {
                      className: menuItemRecipe({ tone }),
                      disabled,
                      'data-testid': option['data-testid'],
                      // Called with NO arguments, which is both what `onSelect` is typed as and what
                      // the legacy `MenuItem` did. Handed straight to `onClick` it received the
                      // MouseEvent, and the event actions take an optional first argument —
                      // `goToEditEvent(args?: EventParams)` spread a SyntheticEvent into route params.
                      onClick: () => onSelect?.(),
                    };

                    return (
                      <Fragment key={id}>
                        {/* Suppressed on the first row: a separator against the panel's own top padding
                        would read as a stray line rather than a group break. */}
                        {separatorBefore && index > 0 && <BaseMenu.Separator className={menuSeparatorRecipe()} />}

                        {selectedId !== undefined ? (
                          // `closeOnClick`: Base UI keeps a radio menu open by default, for a list
                          // you toggle through. These are pickers — choosing is the whole task.
                          <BaseMenu.RadioItem {...itemProps} value={id} closeOnClick>
                            {content}
                            <BaseMenu.RadioItemIndicator className={menuItemCheckRecipe()} data-part="item-check">
                              <SelectCheckMark />
                            </BaseMenu.RadioItemIndicator>
                          </BaseMenu.RadioItem>
                        ) : (
                          <BaseMenu.Item {...itemProps}>{content}</BaseMenu.Item>
                        )}
                      </Fragment>
                    );
                  }
                )}
              </MenuRows>
            )}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
};
